import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { processRequest } from './network/managedRequest';
import { API } from '../constants';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingAreaWithReplacement,
} from './network/managedRequestForAccountingArea';
import { PbRequest } from './pbRequest';
import { PbDispatch } from './index';

interface DefaultContractDto {
    contractual_wewash_share: number;
}

interface ApplianceDefault {
    vib: string;
    type: string;
    group: string;
    control_type: string;
    default_name: string;
    default_manufacturing_batch?: string;
}

export const FALLBACK_DEFAULT_VALUES: DefaultContractDto = {
    contractual_wewash_share: 1,
};

interface AccountingAreaDefaults {
    contractual_type_to_values: {
        [contractualType: string]: DefaultContractDto;
    };
    available_pms_providers: string[];
}

interface MachineInformationDto {
    models: ApplianceDefault[];
    washer_appliance_model_groups: string[];
    dryer_appliance_model_groups: string[];
}

interface AccountingAreaDefaultsStore {
    accountingAreaDefaults?: AccountingAreaDefaults;
    supportedTenants?: SupportedTenantsDtoList;
    applianceDefaults?: MachineInformationDto;
}

export interface SupportedTenantsDto {
    tenant_id: number;
    tenant_name: string;
}

export interface SupportedTenantsDtoList {
    supported_tenants: SupportedTenantsDto[];
}

const initialState: AccountingAreaDefaultsStore = {
    accountingAreaDefaults: undefined,
    supportedTenants: undefined,
    applianceDefaults: undefined,
};

const accountingAreaDefaultsSlice = createSlice({
    name: 'accountingAreaDefaults',
    initialState,
    reducers: {
        setAccountingAreaDefaults(
            state: AccountingAreaDefaultsStore,
            action: PayloadAction<AccountingAreaDefaults>
        ) {
            state.accountingAreaDefaults = action.payload;
        },
        setSupportedTenants(
            state: AccountingAreaDefaultsStore,
            action: PayloadAction<SupportedTenantsDtoList>
        ) {
            state.supportedTenants = action.payload;
        },
        setApplianceDefaults(
            state: AccountingAreaDefaultsStore,
            action: PayloadAction<MachineInformationDto>
        ) {
            state.applianceDefaults = action.payload;
        },
    },
});

export function LoadSupportedTenants() {
    return async (dispatch: PbDispatch) => {
        try {
            const request: RequestInit = {
                method: 'GET',
            };
            const response = await processRequest<SupportedTenantsDtoList>(
                dispatch,
                `${API}/supported_tenants/`,
                request,
                PbRequest.FETCH_SUPPORTED_TENANTS
            );
            dispatch(
                accountingAreaDefaultsSlice.actions.setSupportedTenants(
                    response
                )
            );
        } catch {}
    };
}

export function LoadAccountingAreaDefaults() {
    return async (dispatch: PbDispatch) => {
        try {
            const request: RequestInit = {
                method: 'GET',
            };
            const result = await processRequest<AccountingAreaDefaults>(
                dispatch,
                `${API}/default_contract_details/`,
                request,
                PbRequest.FETCH_ACCOUNTING_AREA_DEFAULTS
            );
            dispatch(
                accountingAreaDefaultsSlice.actions.setAccountingAreaDefaults(
                    result
                )
            );
        } catch {}
    };
}

export function LoadAccountAreaApplianceDefaults() {
    return async (dispatch: PbDispatch) => {
        try {
            const request = { method: 'GET' };
            const response =
                await processRequestForAccountingAreaWithReplacement<MachineInformationDto>(
                    dispatch,
                    `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/appliance-defaults`,
                    request,
                    PbRequest.FETCH_APPLIANCE_DEFAULTS
                );
            dispatch(
                accountingAreaDefaultsSlice.actions.setApplianceDefaults(
                    response
                )
            );
        } catch {}
    };
}

export default accountingAreaDefaultsSlice.reducer;
